export type ColorKey = "primary" | "accent" | "secondary";

export type Benchmark = {
  name: string;
  score: number;
};

export type Principle = {
  name: string;
  icon: string;
  color: ColorKey;
  benchmarks: Benchmark[];
  baseScore: number;
};

export const colorMap: Record<ColorKey, { bg: string; border: string; text: string; iconBg: string; iconText: string }> = {
  primary: {
    bg: "bg-primary-500/10",
    border: "border-primary-500/25",
    text: "text-primary-300",
    iconBg: "bg-primary-500/20",
    iconText: "text-primary-400",
  },
  accent: {
    bg: "bg-accent-500/10",
    border: "border-accent-500/25",
    text: "text-accent-300",
    iconBg: "bg-accent-500/20",
    iconText: "text-accent-400",
  },
  secondary: {
    bg: "bg-secondary-500/10",
    border: "border-secondary-500/25",
    text: "text-secondary-300",
    iconBg: "bg-secondary-500/20",
    iconText: "text-secondary-400",
  },
};

function avg(scores: number[]) {
  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
}

const rawPrinciples: Omit<Principle, "baseScore">[] = [
  {
    name: "Transparency",
    icon: "ri-eye-line",
    color: "primary",
    benchmarks: [
      { name: "Interpretability-PII", score: 84 },
      { name: "Decision-Trace", score: 79 },
      { name: "Chain-of-Reasoning", score: 86 },
      { name: "Model-Card-Completeness", score: 81 },
      { name: "Log-Disclosure", score: 77 },
    ],
  },
  {
    name: "Robustness",
    icon: "ri-shield-line",
    color: "accent",
    benchmarks: [
      { name: "Adversarial-Robustness", score: 72 },
      { name: "OOD-Generalization", score: 68 },
      { name: "Hallucination-Rate", score: 74 },
      { name: "Prompt-Injection-Resistance", score: 81 },
      { name: "Safety-Refusal", score: 79 },
      { name: "Consistency-Across-Runs", score: 83 },
      { name: "Toxicity-Gate", score: 85 },
    ],
  },
  {
    name: "Fairness",
    icon: "ri-scales-3-line",
    color: "secondary",
    benchmarks: [
      { name: "Demographic-Parity", score: 69 },
      { name: "Equalized-Odds", score: 66 },
      { name: "Bias-Benchmark", score: 73 },
      { name: "Stereotype-Detection", score: 71 },
      { name: "Representation-Gap", score: 64 },
      { name: "Counterfactual-Fairness", score: 75 },
    ],
  },
  {
    name: "Human Agency",
    icon: "ri-user-line",
    color: "primary",
    benchmarks: [
      { name: "Human-Oversight", score: 90 },
      { name: "Kill-Switch", score: 86 },
      { name: "Escalation-Path", score: 88 },
      { name: "Override-Integrity", score: 87 },
    ],
  },
  {
    name: "Accountability",
    icon: "ri-folder-chart-line",
    color: "accent",
    benchmarks: [
      { name: "Audit-Trail", score: 82 },
      { name: "Responsibility-Map", score: 78 },
      { name: "Incident-Reporting", score: 80 },
      { name: "Version-Provenance", score: 84 },
    ],
  },
  {
    name: "Privacy",
    icon: "ri-lock-line",
    color: "secondary",
    benchmarks: [
      { name: "PII-Leakage", score: 88 },
      { name: "Memorization-Test", score: 82 },
      { name: "Data-Minimization", score: 85 },
      { name: "Consent-Routing", score: 83 },
    ],
  },
];

export const principles: Principle[] = rawPrinciples.map((p) => ({
  ...p,
  baseScore: avg(p.benchmarks.map((b) => b.score)),
}));

export const totalBenchmarks = principles.reduce((sum, p) => sum + p.benchmarks.length, 0);